import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import CTA from 'Src/components/buttons/CTA';
import { IconCTA } from 'Src/components/buttons/IconCTA';
import { ExpansionSearch } from 'Src/components/ExpansionSearch';
import { OverlayContainer } from 'Src/components/OverlayContainer';
import { TEST_ID } from 'Src/feature/check-create/CheckCreateModal';
import { useCheckCreate } from 'Src/feature/check-create/useCheckCreate';
import { useCheckList } from 'Src/feature/check-list/useCheckList';
import { useCheckListComponents } from 'Src/feature/checks/useCheckListComponents';
import { TableRow } from 'Src/feature/data-table/TableRow';
import { FormFieldSelectProps } from 'Src/feature/form/FormFieldSelect';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useNavigateToCheck } from 'Src/feature/navigate-to-check/useNavigateToCheck';
import { OloConfigModal } from 'Src/feature/olo-config/OloConfigModal';
import { useOloConfig } from 'Src/feature/olo-config/useOloConfig';
import { useCurrentOperator } from 'Src/feature/operator/useCurrentOperator';
import { useTerminalDefaults } from 'Src/feature/terminal/useTerminalDefaults';
import useRoute from 'Src/routes/useRoute';

import { useChecksPage } from './useChecksPage';

import styles from './ChecksList.module.scss';

const sectionId = 'ChecksList';

const sharedSize: FormFieldSelectProps['size'] = 'sm';

const ChecksList: FC = () => {
  const { t } = useTranslation();
  const { crumbs, navigate } = useRoute();
  const store = useCheckList();
  const { Table } = store.table;
  const checksPage = useChecksPage();
  const { navigateToCheck } = useNavigateToCheck();
  const oloConfig = useOloConfig.query.get();
  const oloLeadTimes = useOloConfig.query.getLeadTimes();
  const oloConfigModal = useModalControl();
  const currentOperator = useCurrentOperator();
  const terminalDefaults = useTerminalDefaults();

  const checkCreate = useCheckCreate({ id: sectionId });

  const filters = useCheckListComponents.filters({ store });
  const columns = useCheckListComponents.columns({ store });

  const canEditOloThrottle = currentOperator.permission('pos.olo.updateConfig.update').isAllowed;

  useEffect(() => {
    if (terminalDefaults.view === 'NewCheck' && (terminalDefaults.partySizeType === 'Required' || terminalDefaults.startTabWithCard === 'Enabled')) {
      checkCreate.modal.open('check-list');
    }
    return () => checkCreate.modal.close();
  // empty dependency array because we only want this to run on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={styles.filter_bar}>
        <div className={styles.filters}>
          <ExpansionSearch
            value={store.searchVal}
            onChange={store.setSearchVal}
            isOpen={checksPage.currentSelection === 'searchMode'}
            onClick={store.enableSearchMode}
            size={sharedSize}
            placeholder={t('labels.search')}
          />
          {filters}
        </div>
        <div className={styles.actions}>
          {store.showReset && (
            <CTA
              minWidth="self"
              className={styles.reset_filter_btn}
              icon={{ position: 'start', name:'refresh' }}
              label={t('labels.resetFilters')}
              labelProps={{ line: { type: 'single' } }}
              size={sharedSize}
              onClick={store.handleReset}
            />
          )}
          {checksPage.currentSelection !== 'searchMode'
            ? (
              <>
                {checksPage.currentSelection == 'scheduled' && oloConfig.data?.enabled && canEditOloThrottle && (
                  <CTA
                    data-testid={`${sectionId}-edit-olo-throttling`}
                    labelProps={{ line: { type: 'single' } }}
                    minWidth="self"
                    onClick={oloConfigModal.open}
                    label={t('check.labels.editOlo', { context: oloConfig.data.paused ? 'paused' : '', minutes: oloLeadTimes.data?.takeoutLeadTime })}
                    icon={{ position: 'end', name: 'edit' }}
                  />
                )}
                <CTA
                  className={styles.edit_tip_btn}
                  data-testid={`${sectionId}-edit-tips-btn`}
                  minWidth="self"
                  size={sharedSize}
                  label={t('check.labels.editTips')}
                  labelProps={{ line: { type: 'single' } }}
                  onClick={() => navigate(crumbs.pos.tipEdit.main.path)}
                />
              </>
            ) : (
              <CTA
                size={sharedSize}
                minWidth="self"
                color="primary"
                icon={{ position: 'start', name: 'close' }}
                label={t('labels.done')}
                labelProps={{ line: { type: 'single' } }}
                onClick={store.disableSearchMode}
              />
            )}
        </div>
      </div>
      <Table
        className={styles.table}
        // hide/override pagination w/ blank
        pagination={<></>}
        noDataMessage={t('check.errors.getAll_noData')}
        renderer={{
          row: (dataItem) => ({ children }) => (
            <TableRow onClick={() => navigateToCheck(dataItem.id)}>
              {children}
            </TableRow>
          ),
        }}
      >
        {columns}
      </Table>
      <OverlayContainer position="bottom-right">
        <IconCTA
          data-testid={TEST_ID.action}
          icon="add"
          color="primary"
          padding="xs"
          shadow="sm"
          onClick={() => checkCreate.modal.open('check-list')}
        />
      </OverlayContainer>
      <OloConfigModal open={oloConfigModal.isOpen} onDone={oloConfigModal.close} />
    </>
  );
};

export { ChecksList, sectionId };
